import { apiClient } from "@/lib/api-client";

export type Job = {
  id: string;
  dbId: number;
  title: string;
  title_mr?: string;
  dept: string;
  dept_mr?: string;
  location: string;
  location_mr?: string;
  type: string;
  exp: string;
  exp_mr?: string;
  salary: string;
  deadline: string;
  postedDate: string;
  tag: string | null;
  tagColor: string;
  desc: string;
  desc_mr?: string;
  skills: string;
  skills_mr?: string;
  jobDescription: string;
  jobDescription_mr?: string;
  selectionProcess: string[];
  selectionProcess_mr?: string[];
  employeeBenefits: string[];
  employeeBenefits_mr?: string[];
  minAge: number | null;
  maxAge: number | null;
  ageEligibility: string | null;
  rawPosition: JobPositionRecord;
  appl_close_date: string | null;
  posting_date_raw: string | null;
};

export type JobPositionRecord = {
  post_name: string;
  post_name_mr?: string | null;
  min_salary: string | number | null;
  max_salary: string | number | null;
  salary_type: "Annual" | "Monthly" | "Daily" | "Hourly" | null;
} | null;

export type JobDepartmentRecord = {
  dept_name: string;
  dept_name_mr?: string | null;
} | null;

// Shape returned by GET /job-postings/list and GET /job-postings/:job_opening_code
export type JobApiRecord = {
  id: number;
  job_opening_code: string;
  appl_open_date: string | null;
  appl_close_date: string | null;
  posting_location: string | null;
  posting_location_mr?: string | null;
  employment_type: string | null;
  min_age: number | null;
  max_age: number | null;
  post_role: string | null;
  post_role_mr?: string | null;
  post_responsibilities: string | null;
  post_responsibilities_mr?: string | null;
  required_qualification: string | null;
  required_qualification_mr?: string | null;
  preferred_skills: string | null;
  preferred_skills_mr?: string | null;
  job_description: string | null;
  job_description_mr?: string | null;
  selection_process: string[] | string | null;
  selection_process_mr?: string[] | string | null;
  employee_benefit: string | null;
  employee_benefit_mr?: string | null;
  posting_date: string | null;
  department: JobDepartmentRecord;
  position: JobPositionRecord;
};

type JobApiListResponse = { success: boolean; resp?: JobApiRecord[]; message?: string };
type JobApiDetailsResponse = { success: boolean; resp?: JobApiRecord | null; message?: string };

export function formatDate(value: string | null, lang: "en" | "mr" = "en"): string {
  if (!value) return lang === "mr" ? "नमूद नाही" : "Not specified";
  const locale = lang === "mr" ? "mr-IN" : "en-IN";
  try {
    return new Date(value).toLocaleDateString(locale, {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return new Date(value).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  }
}

export function formatSalary(position: JobPositionRecord, lang: "en" | "mr" = "en"): string {
  const min = position?.min_salary != null ? Number(position.min_salary) : null;
  const max = position?.max_salary != null ? Number(position.max_salary) : null;
  if (!min && !max) {
    return lang === "mr" ? "शासकीय नियमांनुसार" : "As per government norms";
  }

  const unit =
    position?.salary_type === "Annual"
      ? lang === "mr" ? "वर्ष" : "year"
      : position?.salary_type === "Daily"
      ? lang === "mr" ? "दिवस" : "day"
      : position?.salary_type === "Hourly"
      ? lang === "mr" ? "तास" : "hour"
      : lang === "mr" ? "महिना" : "month";

  const fmt = (n: number) => `₹ ${n.toLocaleString("en-IN")}`;

  return min && max ? `${fmt(min)} – ${fmt(max)} / ${unit}` : `${fmt((min ?? max) as number)} / ${unit}`;
}

export function formatEmploymentType(type: string | null | undefined, lang: "en" | "mr" = "en"): string {
  if (!type) return lang === "mr" ? "कायमस्वरूपी" : "Permanent";
  if (lang === "mr") {
    switch (type.toLowerCase()) {
      case "permanent":
        return "कायमस्वरूपी";
      case "contract":
        return "कंत्राटी";
      case "temporary":
        return "तात्पुरते";
      default:
        return type;
    }
  }
  return type;
}

export function formatTag(tag: string | null, lang: "en" | "mr" = "en"): string | null {
  if (!tag) return null;
  if (lang === "mr") {
    if (tag === "Closing soon") return "मुदत लवकरच संपेल";
    if (tag === "New") return "नवीन";
  }
  return tag;
}

function stripHtml(value: string | null | undefined): string {
  if (!value) return "";
  return value
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/\s+/g, " ")
    .trim();
}

function htmlToLines(value: string | null | undefined): string[] {
  if (!value) return [];
  return value
    .replace(/<\/(li|p|div|h[1-6])>/gi, "\n")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .split("\n")
    .map((line) => line.replace(/\s+/g, " ").trim())
    .filter(Boolean);
}

function normalizeSelectionProcess(value: string[] | string | null | undefined): string[] {
  if (Array.isArray(value)) return value.map((stage) => String(stage).trim()).filter(Boolean);
  if (typeof value === "string" && value) return value.split(",").map((stage) => stage.trim()).filter(Boolean);
  return [];
}

export function formatAgeEligibility(minAge: number | null, maxAge: number | null, lang: "en" | "mr" = "en"): string | null {
  if (minAge != null && maxAge != null) {
    return lang === "mr" ? `${minAge} - ${maxAge} वर्षे` : `${minAge} - ${maxAge} years`;
  }
  if (minAge != null) {
    return lang === "mr" ? `${minAge}+ वर्षे` : `${minAge}+ years`;
  }
  if (maxAge != null) {
    return lang === "mr" ? `कमाल ${maxAge} वर्षे` : `Up to ${maxAge} years`;
  }
  return null;
}

function daysFromNow(value: string | null): number | null {
  if (!value) return null;
  return Math.floor((new Date(value).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
}

function deriveTag(record: JobApiRecord): { tag: string | null; tagColor: string } {
  const daysToClose = daysFromNow(record.appl_close_date);
  if (daysToClose !== null && daysToClose >= 0 && daysToClose <= 7) {
    return { tag: "Closing soon", tagColor: "warning" };
  }
  const daysSincePosted = record.posting_date ? -daysFromNow(record.posting_date)! : null;
  if (daysSincePosted !== null && daysSincePosted >= 0 && daysSincePosted <= 7) {
    return { tag: "New", tagColor: "success" };
  }
  return { tag: null, tagColor: "" };
}

function mapJob(record: JobApiRecord): Job {
  const role = stripHtml(record.post_role);
  const roleMr = stripHtml(record.post_role_mr);
  const responsibilities = stripHtml(record.post_responsibilities);
  const responsibilitiesMr = stripHtml(record.post_responsibilities_mr);
  const qualification = stripHtml(record.required_qualification);
  const qualificationMr = stripHtml(record.required_qualification_mr);
  const skills = stripHtml(record.preferred_skills);
  const skillsMr = stripHtml(record.preferred_skills_mr);
  const jobDescription = stripHtml(record.job_description);
  const jobDescriptionMr = stripHtml(record.job_description_mr);
  const selectionProcess = normalizeSelectionProcess(record.selection_process);
  const selectionProcessMr = normalizeSelectionProcess(record.selection_process_mr);
  const employeeBenefits = htmlToLines(record.employee_benefit);
  const employeeBenefitsMr = htmlToLines(record.employee_benefit_mr);
  const minAge = record.min_age != null ? Number(record.min_age) : null;
  const maxAge = record.max_age != null ? Number(record.max_age) : null;

  return {
    id: record.job_opening_code,
    dbId: record.id,
    title: role || record.position?.post_name || "Open position",
    title_mr: roleMr || record.position?.post_name_mr || undefined,
    dept: record.department?.dept_name ?? "General",
    dept_mr: record.department?.dept_name_mr || undefined,
    location: record.posting_location ?? "Mumbai, Maharashtra",
    location_mr: record.posting_location_mr || undefined,
    type: record.employment_type ?? "Permanent",
    exp: qualification || "As per eligibility norms",
    exp_mr: qualificationMr || undefined,
    salary: formatSalary(record.position, "en"),
    deadline: formatDate(record.appl_close_date, "en"),
    postedDate: formatDate(record.posting_date || record.appl_open_date, "en"),
    desc: responsibilities || role || "Details available in the full job posting.",
    desc_mr: responsibilitiesMr || roleMr || jobDescriptionMr || undefined,
    skills,
    skills_mr: skillsMr || undefined,
    jobDescription,
    jobDescription_mr: jobDescriptionMr || undefined,
    selectionProcess,
    selectionProcess_mr: selectionProcessMr.length > 0 ? selectionProcessMr : undefined,
    employeeBenefits,
    employeeBenefits_mr: employeeBenefitsMr.length > 0 ? employeeBenefitsMr : undefined,
    minAge,
    maxAge,
    ageEligibility: formatAgeEligibility(minAge, maxAge, "en"),
    rawPosition: record.position,
    appl_close_date: record.appl_close_date,
    posting_date_raw: record.posting_date || record.appl_open_date,
    ...deriveTag(record),
  };
}

export function getLocalizedJob(job: Job, lang: "en" | "mr" = "en"): Job {
  if (lang === "en") return job;

  return {
    ...job,
    title: job.title_mr || job.title,
    dept: job.dept_mr || job.dept,
    location: job.location_mr || job.location,
    desc: job.desc_mr || job.desc,
    exp: job.exp_mr || job.exp,
    skills: job.skills_mr || job.skills,
    jobDescription: job.jobDescription_mr || job.jobDescription,
    selectionProcess:
      job.selectionProcess_mr && job.selectionProcess_mr.length > 0
        ? job.selectionProcess_mr
        : job.selectionProcess,
    employeeBenefits:
      job.employeeBenefits_mr && job.employeeBenefits_mr.length > 0
        ? job.employeeBenefits_mr
        : job.employeeBenefits,
    salary: formatSalary(job.rawPosition, "mr"),
    deadline: formatDate(job.appl_close_date, "mr"),
    postedDate: formatDate(job.posting_date_raw, "mr"),
    type: formatEmploymentType(job.type, "mr"),
    tag: formatTag(job.tag, "mr"),
    ageEligibility: formatAgeEligibility(job.minAge, job.maxAge, "mr"),
  };
}

export async function getJobs(): Promise<Job[]> {
  const res = await apiClient.get<JobApiListResponse>("/job-postings/list");
  if (!res.data.success || !res.data.resp) return [];
  return res.data.resp.map(mapJob);
}

export async function getJob(id: string): Promise<Job | null> {
  const res = await apiClient.get<JobApiDetailsResponse>(`/job-postings/${encodeURIComponent(id)}`);
  if (!res.data.success || !res.data.resp) return null;
  return mapJob(res.data.resp);
}
